import React, { useState } from "react";
import { RefreshCcw } from "lucide-react";
import { FeedbackProvider, useFeedback } from "../contexts/FeedbackContext";
import FeedbackAnalytics from "../components/feedback/FeedbackAnalytics";
import FeedbackTable from "../components/feedback/FeedbackTable";
import IncidentTable from "../components/feedback/IncidentTable";
import CategoryTable from "../components/feedback/CategoryTable";
import CreateIncidentModal from "../components/feedback/CreateIncidentModal";

const FeedbackManagementContent = () => {
  const {
    canManageCategories,
    feedbackPagination,
    incidentPagination,
    categories,
    triggerRefresh,
    clearFeedbackSelection,
    statsLoading,
    feedbackLoading,
    incidentLoading,
    categoriesLoading,
  } = useFeedback();

  const [activeTab, setActiveTab] = useState("analytics");

  const tabs = [
    { key: "analytics", label: "Analytics" },
    { key: "feedback", label: "Feedback Items", count: feedbackPagination?.total },
    { key: "incidents", label: "Incidents", count: incidentPagination?.total },
    ...(canManageCategories
      ? [{ key: "categories", label: "Categories", count: categories?.length }]
      : []),
  ];

  const isRefreshing =
    activeTab === "analytics" ? statsLoading
    : activeTab === "feedback" ? feedbackLoading
    : activeTab === "incidents" ? incidentLoading
    : categoriesLoading;

  const handleRefresh = () => {
    clearFeedbackSelection();
    triggerRefresh();
  };

  return (
    <div className="flex flex-col h-full lg:h-[calc(100vh-100px)] gap-4 pt-4">
      <div className="flex flex-wrap items-center justify-between gap-3 shrink-0">
      <div className="bg-white rounded-2xl shadow-sm inline-flex items-center gap-0.5 p-1.5 max-w-full overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-3 sm:px-3.5 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap shrink-0 transition-colors ${
              activeTab === tab.key
                ? "bg-slate-900 text-white"
                : "text-gray-500 hover:bg-gray-50"
            }`}
          >
            {tab.label}
            {typeof tab.count === "number" && (
              <span
                className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                  activeTab === tab.key
                    ? "bg-white/20 text-white"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          title="Refresh"
          aria-label="Refresh"
          className={`${activeTab === "analytics" ? "hidden sm:inline-flex" : activeTab === "feedback" || activeTab === "incidents" || activeTab === "categories" ? "hidden sm:inline-flex" : "inline-flex"} items-center justify-center gap-2 h-10 w-10 sm:w-auto sm:px-4 rounded-xl border border-gray-100 bg-white shadow-sm text-sm font-medium text-slate-700 hover:border-gray-300 transition-colors disabled:opacity-60 shrink-0`}
        >
          <RefreshCcw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      <div
        className={`flex-1 min-h-0 -mx-1 px-1 ${
          activeTab === "feedback" || activeTab === "incidents" || activeTab === "categories"
            ? "flex flex-col overflow-hidden"
            : "overflow-y-auto pb-4"
        }`}
      >
        {/* All tabs stay mounted (hidden via CSS instead of unmounted) so each
            one's own fetch-on-mount effect runs immediately on page load —
            this is what makes the count badges populate right away instead
            of only after the user clicks that tab. */}
        <div className={activeTab === "analytics" ? "" : "hidden"}>
          <FeedbackAnalytics />
        </div>
        <div className={`h-full ${activeTab === "feedback" ? "flex flex-col" : "hidden"}`}>
          <FeedbackTable />
        </div>
        <div className={`h-full ${activeTab === "incidents" ? "flex flex-col" : "hidden"}`}>
          <IncidentTable />
        </div>
        {canManageCategories && (
          <div className={`h-full ${activeTab === "categories" ? "flex flex-col" : "hidden"}`}>
            <CategoryTable />
          </div>
        )}
      </div>

      <CreateIncidentModal />
    </div>
  );
};

const FeedbackManagement = () => {
  return (
    <FeedbackProvider>
      <FeedbackManagementContent />
    </FeedbackProvider>
  );
};

export default FeedbackManagement;